/* Review form behaviour.
 *
 * Two hops, and the order matters:
 *   1. For each file: ask the Worker for an upload target, then send the file
 *      STRAIGHT TO SHOPIFY. The bytes never touch our Worker — Cloudflare caps a
 *      request body at 100 MB and a phone video goes past that.
 *   2. Post the review as JSON, carrying only the resulting references.
 *
 * Uploads happen before the review is submitted, so a rejected file is reported
 * while the customer still has their text on screen. Nothing they typed is ever
 * cleared on an error.
 *
 * THIS FILE IS REQUIRED. There is no no-JavaScript path: a Shopify theme cannot
 * receive a POST, so the form has no action to fall back to and submitting without
 * this script would reload the page. That is why the markup stays a real <form>
 * with real validation attributes — the browser still does the easy checks — but
 * the send itself is fetch, here.
 */
(function () {
  'use strict';

  var MAX_PHOTOS = 3;
  var MAX_PHOTO_BYTES = 5e6;
  var MAX_VIDEO_BYTES = 1e8;
  var MAX_VIDEO_SECONDS = 60;
  var PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  var VIDEO_TYPES = ['video/mp4', 'video/quicktime'];

  /* Turnstile is loaded with render=explicit, so the widget appears only when the
   * form is actually revealed. The script is async: when the form opens first, wait
   * for it rather than giving up on a challenge the submit will then be rejected
   * for missing. */
  function whenTurnstileReady(callback) {
    if (window.turnstile) { callback(); return; }
    var waited = 0;
    var timer = setInterval(function () {
      waited += 100;
      if (window.turnstile) { clearInterval(timer); callback(); }
      else if (waited >= 10000) { clearInterval(timer); }
    }, 100);
  }

  /* On the standalone /pages/write-a-review, the product and the signed link come
   * from the URL — Liquid cannot read query parameters, so the page fills itself in.
   * On a product page there is no query string and none of this runs. */
  async function hydrateFromLink(form) {
    var params = new URLSearchParams(window.location.search);
    var handle = params.get('p');
    var token = params.get('t');
    var context = document.querySelector('[data-review-context]');
    if (!handle && !token) return;

    if (handle) {
      form.dataset.product = handle;
      var manual = form.querySelector('[name="productHandle"]');
      if (manual) manual.closest('.review-form__field').hidden = true;

      /* Show the real product name, not the handle. If the handle is wrong this
       * 404s, and we say so and reveal the manual field again rather than letting
       * someone write a review that cannot be filed. */
      try {
        var res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
        if (!res.ok) throw new Error('unknown product');
        var product = await res.json();
        if (context) {
          context.innerHTML = 'You are reviewing <b></b>.';
          context.querySelector('b').textContent = product.title;
          context.hidden = false;
        }
      } catch (e) {
        form.dataset.product = '';
        if (manual) manual.closest('.review-form__field').hidden = false;
        if (context) {
          context.textContent = 'We could not find that product — please tell us which one below.';
          context.hidden = false;
        }
      }
    }

    if (!token) return;
    try {
      var verified = await fetch(form.dataset.worker + '/token/verify?t=' + encodeURIComponent(token));
      var result = await verified.json();
      if (!result.valid) {
        /* An expired link is not the customer's fault and must not read like a
         * rejection. The form still works; the review is simply unverified. */
        if (result.reason === 'expired' && context) {
          context.textContent = 'That link has expired — you can still leave your review below.';
          context.hidden = false;
        }
        return;
      }
      form.dataset.token = token;
      var email = form.querySelector('[name="authorEmail"]');
      if (email && result.authorEmail) email.value = result.authorEmail;
      if (result.productHandle) form.dataset.product = result.productHandle;
      /* A proven link means no Turnstile: the token already shows an order exists. */
      var widget = form.querySelector('[data-turnstile]');
      if (widget) widget.remove();
    } catch (e) {
      /* Verification unreachable: fall through as unverified rather than blocking. */
    }
  }

  document.querySelectorAll('.review-form').forEach(function (form) {
    var worker = form.dataset.worker;
    if (!worker) return;

    /* Bind once per form, however many times this file runs.
     *
     * The snippet emits its own <script src>, so a page holding two review forms
     * loads and EXECUTES this file twice, and every listener below is attached
     * twice. For the open/close toggle that is not a slow leak but an immediate
     * bug: two handlers flip aria-expanded twice on one click and the panel never
     * moves. An even number of copies breaks it, an odd number hides it — which is
     * exactly how it passed testing until a page with six forms appeared.
     *
     * A product page carries one form today. That is not a guarantee worth relying
     * on: a page can hold the product section and a write-a-review section at once,
     * and Shopify re-renders sections in the theme editor. */
    if (form.dataset.reviewFormReady === '1') return;
    form.dataset.reviewFormReady = '1';

    var hydrated = hydrateFromLink(form);

    var status = form.querySelector('.review-form__status');
    var submit = form.querySelector('.review-form__submit');
    var fields = form.querySelector('.review-form__fields');
    var done = form.querySelector('.review-form__done');
    var fileInput = form.querySelector('.review-form__file');
    var fileList = form.querySelector('[data-file-list]');
    /* The panel wraps the form; the button that opens it lives somewhere else
     * entirely — the top-right of the ratings summary card — and points here with
     * aria-controls. That attribute is the only link between them, which is why
     * the panel needs an id and why this looks the button up rather than the other
     * way round. */
    var panel = form.closest('[data-review-panel]');
    var toggle = panel && panel.id
      ? document.querySelector('[data-review-toggle][aria-controls="' + CSS.escape(panel.id) + '"]')
      : null;

    /* Measured from when the form was REVEALED, not when the page loaded. Behind a
     * disclosure those are minutes apart, and a minimum fill time measured from
     * page load would pass for anyone who opened the form and pasted. */
    var renderedAt = Math.floor(Date.now() / 1000);

    /* --- Turnstile ------------------------------------------------------- */

    var turnstileId = null;
    var turnstileAsked = false;

    function mountTurnstile() {
      if (turnstileAsked) return;
      turnstileAsked = true;
      /* Wait for the link check: a proven token removes the widget entirely, and
       * rendering one first would put a challenge on screen only to delete it. */
      hydrated.then(function () {
        if (form.dataset.token) return;
        var box = form.querySelector('[data-turnstile]');
        if (!box || !box.isConnected) return;
        whenTurnstileReady(function () {
          if (!box.isConnected) return;
          turnstileId = window.turnstile.render(box, {
            sitekey: box.dataset.sitekey,
            theme: 'light',
          });
        });
      });
    }

    /* --- open and close --------------------------------------------------- */

    if (panel && toggle) {
      /* The panel ships hidden and the button ships aria-expanded="false", so a
       * page whose script never ran shows neither an open form nor a broken one.
       * There is no working no-JS path to preserve here: a Shopify theme has
       * nowhere to POST to, so a form left visible would be one a customer could
       * fill in and never send. */
      var setOpen = function (open) {
        toggle.setAttribute('aria-expanded', String(open));
        panel.hidden = !open;
        if (!open) return;
        renderedAt = Math.floor(Date.now() / 1000);
        mountTurnstile();
      };
      toggle.addEventListener('click', function () {
        setOpen(toggle.getAttribute('aria-expanded') !== 'true');
      });
      /* Focus stays on the button, per the WAI-ARIA disclosure pattern. The panel
       * opens directly beneath it and is already in view, so moving focus would
       * only cost a keyboard user their place. */
      if (toggle.getAttribute('aria-expanded') === 'true') setOpen(true);
    } else {
      mountTurnstile();
    }

    /* --- star input ------------------------------------------------------ */

    /* Paint the stars. CSS can only select FOLLOWING siblings, so "fill up to the
     * chosen one" in natural DOM order needs this — and natural order is what keeps
     * the arrow keys moving the way the row looks. focusin is included so a keyboard
     * user sees the same preview a mouse user does. */
    var stars = form.querySelector('.review-form__stars');
    var ratingValue = form.querySelector('[data-rating-value]');
    if (stars) {
      var paint = function (attr, value) { stars.setAttribute(attr, value || '0'); };
      var readout = function (value) {
        if (!ratingValue) return;
        ratingValue.textContent = value ? value + ' out of 5' : '';
      };
      stars.addEventListener('change', function (e) {
        paint('data-rating', e.target.value);
        readout(e.target.value);
      });
      stars.addEventListener('focusin', function (e) { paint('data-hover', e.target.value); });
      stars.addEventListener('focusout', function () { stars.removeAttribute('data-hover'); });
      stars.addEventListener('mouseover', function (e) {
        var label = e.target.closest('label');
        if (label) paint('data-hover', form.querySelector('#' + CSS.escape(label.htmlFor)).value);
      });
      stars.addEventListener('mouseleave', function () { stars.removeAttribute('data-hover'); });
    }

    /* --- chosen files ----------------------------------------------------- */

    /* The native input is hidden, so without this nothing on screen changes when a
     * file is picked and there is no way to tell whether it took. */
    function renderFileList() {
      if (!fileList || !fileInput) return;
      var files = Array.prototype.slice.call(fileInput.files);
      fileList.textContent = '';
      fileList.hidden = files.length === 0;
      files.forEach(function (file) {
        var item = document.createElement('li');
        var name = document.createElement('span');
        name.className = 'review-form__file-name';
        name.textContent = file.name;
        var size = document.createElement('span');
        size.className = 'review-form__file-size';
        size.textContent = (file.size / 1e6).toFixed(1) + ' MB';
        item.appendChild(name);
        item.appendChild(size);
        fileList.appendChild(item);
      });
      if (files.length) {
        var clear = document.createElement('li');
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'review-form__file-clear';
        button.textContent = files.length === 1 ? 'Remove file' : 'Remove all';
        button.addEventListener('click', function () {
          fileInput.value = '';
          renderFileList();
          fileInput.focus();
        });
        clear.appendChild(button);
        fileList.appendChild(clear);
      }
    }

    if (fileInput) fileInput.addEventListener('change', renderFileList);

    function say(message, isError) {
      status.textContent = message;
      status.classList.toggle('is-error', Boolean(isError));
    }

    /* Duration is checked here because only the browser knows it. Rejecting a
     * four-minute clip AFTER it uploads is the worst possible order. */
    function videoSeconds(file) {
      return new Promise(function (resolve) {
        var url = URL.createObjectURL(file);
        var probe = document.createElement('video');
        probe.preload = 'metadata';
        probe.onloadedmetadata = function () {
          URL.revokeObjectURL(url);
          resolve(probe.duration);
        };
        probe.onerror = function () {
          URL.revokeObjectURL(url);
          resolve(null); // unreadable: let the Worker decide rather than guess
        };
        probe.src = url;
      });
    }

    async function checkFiles(files) {
      var photos = files.filter(function (f) { return PHOTO_TYPES.indexOf(f.type) !== -1; });
      var videos = files.filter(function (f) { return VIDEO_TYPES.indexOf(f.type) !== -1; });

      if (photos.length + videos.length !== files.length) {
        return 'Photos can be JPEG, PNG or WebP, and videos MP4 or MOV.';
      }
      if (photos.length && videos.length) return 'Please attach photos or a video, not both.';
      if (videos.length > 1) return 'One video per review, please.';
      if (photos.length > MAX_PHOTOS) return 'Up to ' + MAX_PHOTOS + ' photos, please.';

      for (var i = 0; i < photos.length; i += 1) {
        if (photos[i].size > MAX_PHOTO_BYTES) {
          return 'Photos need to be under 5 MB. "' + photos[i].name + '" is ' +
            (photos[i].size / 1e6).toFixed(1) + ' MB.';
        }
      }
      if (videos.length) {
        if (videos[0].size > MAX_VIDEO_BYTES) {
          return 'Videos need to be under 100 MB. That one is ' +
            (videos[0].size / 1e6).toFixed(1) + ' MB.';
        }
        var seconds = await videoSeconds(videos[0]);
        if (seconds !== null && seconds > MAX_VIDEO_SECONDS + 1) {
          return 'Videos need to be under ' + MAX_VIDEO_SECONDS + ' seconds. That one is ' +
            Math.round(seconds) + '.';
        }
      }
      return null;
    }

    /* Ask for a target, then POST the file to Shopify. Every parameter Shopify
     * returns must be appended BEFORE the file field — the storage layer rejects
     * the upload otherwise, and the error it gives does not say so. */
    async function uploadOne(file, index, total) {
      say('Uploading ' + (total > 1 ? (index + 1) + ' of ' + total : file.name) + '…');

      var signed = await fetch(worker + '/media/sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, mimeType: file.type, fileSize: file.size }),
      });
      var target = await signed.json();
      if (!signed.ok) throw new Error(target.error || 'That file could not be accepted.');

      var body = new FormData();
      target.parameters.forEach(function (p) { body.append(p.name, p.value); });
      body.append('file', file);

      var put = await fetch(target.url, { method: 'POST', body: body });
      if (!put.ok) throw new Error('That file did not upload. Please try again.');

      return { resourceUrl: target.resourceUrl, kind: target.kind };
    }

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      say('');

      if (!form.querySelector('input[name="rating"]:checked')) {
        say('Please choose a rating.', true);
        return;
      }
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      var files = fileInput ? Array.prototype.slice.call(fileInput.files) : [];
      var fileProblem = await checkFiles(files);
      if (fileProblem) { say(fileProblem, true); return; }

      submit.disabled = true;
      try {
        var media = [];
        for (var i = 0; i < files.length; i += 1) {
          media.push(await uploadOne(files[i], i, files.length));
        }

        say('Sending…');
        var data = new FormData(form);
        var turnstile = form.querySelector('[name="cf-turnstile-response"]');

        var response = await fetch(worker + '/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productHandle: form.dataset.product || data.get('productHandle') || '',
            rating: Number(data.get('rating')),
            title: data.get('title') || '',
            body: data.get('body') || '',
            authorDisplayName: data.get('authorDisplayName') || '',
            authorEmail: data.get('authorEmail') || '',
            media: media,
            token: form.dataset.token || undefined,
            turnstileToken: turnstile ? turnstile.value : undefined,
            honeypot: data.get('website') || '',
            renderedAt: renderedAt,
          }),
        });

        var result = await response.json().catch(function () { return {}; });
        if (!response.ok) throw new Error(result.error || 'Something went wrong. Please try again.');

        /* One success state, always. The Worker deliberately does not tell us
         * whether this published or queued, so there is nothing to branch on. */
        fields.hidden = true;
        done.hidden = false;
        done.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch (error) {
        say(error.message || 'Something went wrong. Please try again.', true);
        submit.disabled = false;
        /* Turnstile tokens are single-use: a retry after a failure needs a fresh
         * one, or the second attempt is rejected for a reason the customer cannot
         * see and cannot fix. Reset THIS widget — a page can hold more than one
         * form, and the bare turnstile.reset() resets whichever it rendered last. */
        if (window.turnstile && turnstileId !== null) window.turnstile.reset(turnstileId);
      }
    });
  });
})();
